import type { Conversation } from '@botpress/client'
import { useQuery, useSuspenseQuery } from '@tanstack/react-query'
import { MouseEventHandler, useMemo } from 'react'
import {
  getIntegrationByNameQueryOptions,
  conversationQueryOptions,
  listConversationUsersQueryOptions,
} from '~/queries'
import { DateTime } from 'luxon'
import { Avatar, Icon, Badge } from '~/elements'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { sentimentStyles, sentimentLabels } from './index'
import { minimalFormatDate } from '@bpinternal/shared'
import { cn } from '~/utils'

type Props = {
  conversations: Conversation[]
  workspaceId: string
  botId: string
  selectedConversationId?: string
  selectedConvRef: React.Ref<HTMLDivElement>
}

export const ConversationsTable = ({ conversations, selectedConversationId, selectedConvRef, ...props }: Props) => {
  const navigate = useNavigate()
  const search = useSearch({ strict: false })

  return (
    <div className="flex w-full flex-col gap-12 md:gap-2">
      {conversations.map((conversation) => (
        <ConversationRow
          convRef={selectedConversationId === conversation.id ? selectedConvRef : null}
          key={conversation.id}
          onClick={() => {
            navigate({
              to: '/workspaces/$workspaceId/bots/$botId/conversations/$conversationId',
              params: {
                conversationId: conversation.id,
                ...props,
              },
              search: search,
            })
          }}
          isSelected={selectedConversationId === conversation.id}
          {...conversation}
          {...props}
        />
      ))}
    </div>
  )
}

type ConversationRowProps = Conversation & {
  workspaceId: string
  botId: string
  onClick: MouseEventHandler
  isSelected: boolean
  convRef: React.Ref<HTMLDivElement>
}
const ConversationRow = ({
  botId,
  workspaceId,
  onClick,
  id,
  integration,
  updatedAt,
  isSelected,
  convRef,
}: ConversationRowProps) => {
  const {
    data: { conversation },
  } = useSuspenseQuery(conversationQueryOptions({ botId, workspaceId, conversationId: id }))
  const {
    data: { users },
  } = useSuspenseQuery(listConversationUsersQueryOptions({ conversationId: id, botId, workspaceId }))
  const { data: fetchedIntegration } = useQuery(getIntegrationByNameQueryOptions({ name: integration, workspaceId }))

  const firstUser = users?.at(0)
  const isPending = conversation.tags.isDirty === 'true'
  const hasTitle = !!conversation.tags.title
  const baseTitleStyle = 'mr-auto line-clamp-1 text-sm'

  const IntegrationIcon = useMemo(
    () => (props: { className?: string }) => <img src={fetchedIntegration?.iconUrl} {...props} />,
    [fetchedIntegration?.iconUrl]
  )

  return (
    <div
      ref={convRef}
      onClick={onClick}
      className={`flex cursor-pointer items-center gap-4 rounded-lg hover:bg-gray-3 md:px-4 md:py-5 ${isSelected ? 'bg-gray-3' : ''}`}
    >
      {firstUser && <Avatar name={firstUser?.name ?? 'Anonymous User'} pictureUrl={firstUser?.pictureUrl} />}
      <div className="flex w-full flex-col gap-2">
        <div className="flex items-center gap-2">
          <p className="text-sm font-medium">{firstUser?.name ?? 'Anonymous User'}</p>
          <p className="text-xs text-muted-foreground">{conversation.tags.message_count}</p>
          <p className="ml-auto text-xs text-muted-foreground">
            {minimalFormatDate(DateTime.fromISO(updatedAt).toJSDate())}
          </p>
        </div>
        <div className="flex items-center justify-between gap-1">
          {hasTitle ? (
            <p className={baseTitleStyle}>{conversation.tags.title}</p>
          ) : isPending ? (
            <p className={cn(baseTitleStyle, 'h-4 flex-1 rounded-full', isSelected ? 'bg-gray-4' : 'bg-gray-3')}></p>
          ) : (
            <p className={cn(baseTitleStyle, 'text-muted-foreground')}>Conversation Topic Unknown</p>
          )}
          {(conversation.tags.sentiment || isPending) && (
            <Badge
              variant="secondary"
              className={cn('ml-2 min-w-fit border-none', sentimentStyles[conversation.tags.sentiment ?? 'pending'])}
            >
              {sentimentLabels[conversation.tags.sentiment ?? 'pending']}
            </Badge>
          )}
          <Icon size="3" variant={'outline'} icon={IntegrationIcon} />
        </div>
      </div>
    </div>
  )
}
